import React, { useContext, useRef, useState } from 'react';
import { ApiContext } from '@api/api';
import { isNullOrUndefined } from '@utils/utils';
import { Button } from 'primereact/button';
import ManageInheritedModulesSidebar from './ManageInheritedModulesSidebar/ManageInheritedModulesSidebar';
import EditButton from '@components/obsolete/Buttons/EditButton/EditButton.tsx';
import useTranslationLgs from '@hooks/i18n/useTranslation.tsx';
import SectionPanel from '@components/framework/panels/SectionPanel.tsx';
import RingilDataTable, { ColumnConfig } from '@components/RingilDataTable/RingilDataTable.tsx';
import SvgBuilding from '@icons/building.svg?react';

interface ComponentProps {
  companyId: string;
}

interface Company {
  name: string;
  companyId: string;
  inheritedModules: string[];
}

interface VisibleDialog {
  visible: boolean;
  inheriterCompanyId?: string;
}

const AdminCompanyInheriters: React.FC<ComponentProps> = ({ companyId }) => {
  const { adminListInheritingCompaniesByCompany } = useContext(ApiContext);
  const [visibleAddDialog, setVisibleAddDialog] = useState<VisibleDialog>({
    visible: false,
  });
  const { tr } = useTranslationLgs();
  const tableRef = useRef<{ reloadData: () => void; selectedObjects: unknown[]; clear: () => void }>(null);

  // One row per inheriting company: the endpoint returns one row per company/module pair.
  const groupByCompany = (rows: { companyId: string; companyName: string; companyModule: string }[]): Company[] =>
    rows
      .reduce((previousValue, currentValue) => {
        const company = previousValue.find(t => t.companyId === currentValue.companyId);

        if (isNullOrUndefined(company)) {
          return [
            ...previousValue,
            {
              name: currentValue.companyName,
              companyId: currentValue.companyId,
              inheritedModules: [currentValue.companyModule],
            } as Company,
          ];
        } else {
          return [
            ...previousValue.filter(t => t.companyId !== currentValue.companyId),
            {
              name: currentValue.companyName,
              companyId: currentValue.companyId,
              inheritedModules: [...company.inheritedModules, currentValue.companyModule],
            } as Company,
          ];
        }
      }, [] as Company[])
      .sort((a, b) => a.name.localeCompare(b.name));

  const columnConfig: ColumnConfig<Company>[] = [
    {
      id: 'companyId',
      header: 'adminCompanyDetail.companyId',
      headerDefault: 'Company ID',
      verticalAlign: 'middle',
      render: row => <div>{row.companyId}</div>,
    },
    {
      id: 'name',
      header: 'adminCompanyDetail.companyName',
      headerDefault: 'Company name',
      verticalAlign: 'middle',
      render: row => <div>{row.name}</div>,
    },
    {
      id: 'inheritedModules',
      header: 'adminCompanyDetail.modules',
      headerDefault: 'Modules',
      verticalAlign: 'middle',
      render: row => <div>{row.inheritedModules.join(', ')}</div>,
    },
    {
      id: 'actions',
      width: '10rem',
      verticalAlign: 'middle',
      render: row => (
        <EditButton
          label={tr('adminCompanyDetail.manageInheritedModules', 'Manage')}
          onClick={() =>
            setVisibleAddDialog({
              inheriterCompanyId: row.companyId,
              visible: true,
            })
          }
        />
      ),
    },
  ];

  return (
    <SectionPanel
      icon={<SvgBuilding className={'w-6 h-6'} />}
      iconColor={'var(--content-tertiary)'}
      heading={tr('adminCompanyDetail.inheritingCompanies', 'Inherited companies')}
      headerRight={
        <Button
          label={tr('adminCompanyDetail.addInheritingCompany', 'Add company')}
          onClick={() => setVisibleAddDialog({ visible: true, inheriterCompanyId: null })}
        />
      }
      dataTestId={'admin-company-section-inheriters'}>
      <RingilDataTable
        ref={tableRef}
        columnConfig={columnConfig as ColumnConfig<unknown>[]}
        filterName={'admin_company_inheriters'}
        hidePaginator={true}
        loadDataFromApi={(filter, pageable, onSuccess) =>
          adminListInheritingCompaniesByCompany(companyId, d => {
            const grouped = groupByCompany(d ?? []);
            onSuccess({ data: grouped, total: grouped.length, page: 1, size: grouped.length });
          })
        }
      />

      <ManageInheritedModulesSidebar
        ownerCompanyId={companyId}
        visible={visibleAddDialog.visible}
        inheriterCompanyId={visibleAddDialog.inheriterCompanyId}
        onHide={() => {
          tableRef.current?.reloadData();
          setVisibleAddDialog({ inheriterCompanyId: null, visible: false });
        }}
      />
    </SectionPanel>
  );
};

export default AdminCompanyInheriters;
